import { useLayoutEffect, useState, type RefObject } from 'react';
import type { CardFooterTagVisibility } from 'src/modules/communications/types/card-footer-tag-visibility.type';

/**
 * Measures whole badges independently of the absolutely positioned toggle.
 * @param labels - Visible label identity used to remeasure content updates.
 * @param toggleRef - Mounted toggle whose actual width is reserved for overflow.
 * @param enabled - Whether desktop fitting measurements are needed.
 * @returns Viewport ref, overflow state, fitting badge indexes, and toggle offset.
 */
export const useCardFooterTagVisibility = (
  labels: string,
  toggleRef: RefObject<HTMLButtonElement>,
  enabled = true
): CardFooterTagVisibility => {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [visibility, setVisibility] = useState<
    Omit<CardFooterTagVisibility, 'ref'>
  >({
    isOverflowing: false,
    visibleIndexes: [],
    toggleLeft: 1,
  });

  useLayoutEffect(() => {
    if (!enabled) return;
    const row = element?.firstElementChild;
    const toggle = toggleRef.current;
    if (!element || !(row instanceof HTMLElement) || !toggle) return;

    const measure = () => {
      const badges = Array.from(row.children);
      const rowBounds = row.getBoundingClientRect();
      const viewportWidth = element.getBoundingClientRect().width;
      const isOverflowing =
        badges.length > 0 && rowBounds.width > viewportWidth;
      // The hidden toggle stays measurable without changing the viewport width.
      const availableWidth =
        viewportWidth -
        (isOverflowing ? toggle.getBoundingClientRect().width + 8 : 0);
      const visibleIndexes: number[] = [];
      let usedWidth = 1;
      for (const [index, badge] of badges.entries()) {
        const nextWidth =
          usedWidth +
          (visibleIndexes.length > 0 ? 8 : 0) +
          badge.getBoundingClientRect().width;
        // Skipping a wide badge can leave room for a later complete badge.
        // Keep the final pixel of padding so every visible outline stays intact.
        if (!isOverflowing || nextWidth + 1 <= availableWidth) {
          visibleIndexes.push(index);
          usedWidth = nextWidth;
        }
      }
      const toggleLeft = visibleIndexes.length > 0 ? usedWidth + 8 : 1;
      setVisibility((previous) =>
        previous.isOverflowing === isOverflowing &&
        previous.visibleIndexes.length === visibleIndexes.length &&
        previous.visibleIndexes.every(
          (index, position) => index === visibleIndexes[position]
        ) &&
        previous.toggleLeft === toggleLeft
          ? previous
          : { isOverflowing, visibleIndexes, toggleLeft }
      );
    };

    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    observer.observe(row);
    observer.observe(toggle);
    return () => observer.disconnect();
  }, [element, labels, toggleRef, enabled]);

  return {
    ref: setElement,
    ...visibility,
    isOverflowing: enabled && visibility.isOverflowing,
  };
};
